import type { Block } from './industries';

/*
 * The PDF of a template, made from the same `document` block the page shows (no second copy of the text). A small writer for plain text in the standard
 * Helvetica faces (nothing to embed): it wraps lines, starts a new page when one is full, and keeps to characters those faces have.
 */
type DocumentBlock = Extract<Block, { type: 'document' }>;

// Helvetica advance widths for the characters from space (32) to tilde (126), in 1/1000 em.
const W = [
  278, 278, 355, 556, 556, 889, 667, 191, 333, 333, 389, 584, 278, 333, 278, 278, 556, 556, 556, 556, 556, 556, 556, 556, 556, 556, 278, 278, 584, 584, 584, 556,
  1015, 667, 667, 722, 722, 667, 611, 778, 722, 278, 500, 667, 556, 833, 722, 778, 667, 778, 722, 667, 611, 722, 667, 944, 667, 667, 611, 278, 278, 278, 469, 556,
  333, 556, 556, 500, 556, 556, 278, 556, 556, 222, 222, 500, 222, 833, 556, 556, 556, 556, 333, 500, 278, 556, 500, 722, 500, 500, 500, 334, 260, 334, 584,
];

/** Typographic characters become their plain twins; anything else the standard fonts lack becomes "?". */
const plain = (s: string) =>
  s
    .replace(/[‘’]/g, "'")
    .replace(/[“”]/g, '"')
    .replace(/[–—]/g, '-')
    .replace(/…/g, '...')
    .replace(new RegExp(String.fromCharCode(92) + 'u00A0', 'g'), ' ')
    .replace(/[^\x20-\x7E]/g, '?');

const width = (s: string, size: number, bold: boolean) => (s.split('').reduce((n, c) => n + (W[c.charCodeAt(0) - 32] ?? 556), 0) / 1000) * size * (bold ? 1.08 : 1);
const escape = (s: string) => s.replace(/\\/g, '\\\\').replace(/\(/g, '\\(').replace(/\)/g, '\\)');

function wrap(text: string, size: number, bold: boolean, max: number): string[] {
  const out: string[] = [];
  let line = '';
  for (const word of plain(text).split(/\s+/).filter(Boolean)) {
    const next = line ? `${line} ${word}` : word;
    if (line && width(next, size, bold) > max) {
      out.push(line);
      line = word;
    } else line = next;
  }
  if (line) out.push(line);
  return out;
}

interface Run { text: string; size: number; bold: boolean; gap: number }

/** The text of the PDF, by page. Exported for tests. */
export function layoutTemplate(title: string, doc: DocumentBlock): { pages: string[][]; text: string } {
  const runs: Run[] = [{ text: title, size: 16, bold: true, gap: 10 }];
  for (const s of doc.sections) {
    runs.push({ text: s.heading, size: 12, bold: true, gap: 6 });
    for (const l of s.lines) runs.push({ text: l, size: 11, bold: false, gap: 4 });
  }
  const pageW = 612, pageH = 792, margin = 54, max = pageW - 2 * margin;
  const pages: string[][] = [[]];
  let y = pageH - margin;
  let ops: string[] = pages[0];
  const text: string[] = [];
  for (const r of runs) {
    const lead = r.size * 1.35;
    const lines = wrap(r.text, r.size, r.bold, max);
    y -= r.gap;
    for (const line of lines) {
      if (y - lead < margin) {
        ops = [];
        pages.push(ops);
        y = pageH - margin;
      }
      y -= lead;
      ops.push(`BT /${r.bold ? 'F2' : 'F1'} ${r.size} Tf ${margin} ${y.toFixed(1)} Td (${escape(line)}) Tj ET`);
      text.push(line);
    }
  }
  return { pages, text: text.join('\n') };
}

/** A valid single-font-family PDF (Letter size) of the template. */
export function templatePdf(title: string, doc: DocumentBlock): Blob {
  const { pages } = layoutTemplate(title, doc);
  const objects: string[] = [];
  // 1 catalog, 2 pages, 3 Helvetica, 4 Helvetica-Bold, then a page + a content stream for each page
  const kids = pages.map((_, i) => `${5 + i * 2} 0 R`).join(' ');
  objects.push('<< /Type /Catalog /Pages 2 0 R >>');
  objects.push(`<< /Type /Pages /Kids [${kids}] /Count ${pages.length} >>`);
  objects.push('<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica /Encoding /WinAnsiEncoding >>');
  objects.push('<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica-Bold /Encoding /WinAnsiEncoding >>');
  pages.forEach((ops, i) => {
    objects.push(`<< /Type /Page /Parent 2 0 R /MediaBox [0 0 612 792] /Resources << /Font << /F1 3 0 R /F2 4 0 R >> >> /Contents ${6 + i * 2} 0 R >>`);
    const stream = ops.join('\n');
    objects.push(`<< /Length ${stream.length} >>\nstream\n${stream}\nendstream`);
  });
  let out = '%PDF-1.4\n';
  const offsets: number[] = [];
  objects.forEach((o, i) => {
    offsets.push(out.length);
    out += `${i + 1} 0 obj\n${o}\nendobj\n`;
  });
  const xref = out.length;
  out += `xref\n0 ${objects.length + 1}\n0000000000 65535 f \n${offsets.map((o) => `${String(o).padStart(10, '0')} 00000 n \n`).join('')}`;
  out += `trailer\n<< /Size ${objects.length + 1} /Root 1 0 R >>\nstartxref\n${xref}\n%%EOF\n`;
  return new Blob([out], { type: 'application/pdf' });
}
